import GlobalNavAvatar from '@assets/global_nav_avatar.svg';
import AuthModule from '@modules/AuthModule';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { useDiagnostics } from '@modules/DiagnosticsModule/DiagnosticsContext';
import useService from '@modules/ServiceModule/useService';
import {
  ActionMenu,
  BodyText,
  Button,
  Stack,
  StatusLight,
  Tooltip,
} from '@servicetitan/design-system';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import DiagnosticsModule from '@modules/DiagnosticsModule';
import { useBugReportContext } from '@components/BugReportProvider/useBugReportContext';
import { mainLayoutLocators } from '@locators/main-layout';
import { strings } from './strings';
import styles from './UserMenu.module.css';
import classNames from 'classnames';
import UserStatusModule from '@modules/UserStatusModule';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import { hasValidSeat } from '@constants/user';
import usePermissions from '@app/permissions/usePermissions';
import { permissions } from '@app/permissions/permissions';

export function UserMenu() {
  const service = useService();
  const { user } = useAuth();
  const { callList } = usePhoneState();
  const {
    [FeatureFlagKeys.featureRefreshUserStatus]: refreshUserStatusEnabled,
    [FeatureFlagKeys.featureMonitorAndResetUserStatus]:
      monitorAndResetUserStatusEnabled,
    [FeatureFlagKeys.featureWebphoneNetworkTest]: webphoneNetworkTestEnabled,
  } = useFeatureFlags();
  const hasNetworkTestPermission = usePermissions(
    [permissions.general.ACTION_CALL_OPTIONS.NETWORK_TEST.VIEW],
    'all',
  );

  const showRefreshUserStatusMenuItem =
    refreshUserStatusEnabled && !monitorAndResetUserStatusEnabled;
  const showNetworkTestMenuItem =
    webphoneNetworkTestEnabled &&
    hasNetworkTestPermission &&
    hasValidSeat(user?.seatLevel);
  const hasOngoingCall = callList.some((call) => call.hasSession);
  const [open, setOpen] = useState(false);
  const { errors } = useDiagnostics();
  const navigate = useNavigate();
  const { createBugReport } = useBugReportContext();

  const handleNavigate = () => {
    setOpen(false);
  };

  const handleSignout = async () => {
    setOpen(false);
    await AuthModule.signout();
    navigate('/');
  };

  const handleShowDiagnostics = () => {
    DiagnosticsModule.setDiagnosticsDrawerOpen(true);
  };

  const handleBugReport = () => {
    createBugReport();
  };

  const handleRefreshUserStatus = async () => {
    UserStatusModule.attemptRefreshUserStatus();
    setOpen(false);
  };

  const handleMakeTestCall = () => {
    if (hasOngoingCall) {
      return;
    }
    setOpen(false);
    SoftPhoneModule.startNetworkTest();
  };

  if (!service) {
    return null;
  }

  const unreadErrors = errors.filter((error) => !error.read);

  return (
    <ActionMenu
      el='span'
      open={open}
      id={mainLayoutLocators.userMenu.openButton}
      trigger={
        <Tooltip el='div' text='User Settings'>
          <Button
            className={classNames(
              'c-white',
              'p-x-1',
              'm-r-2',
              'm-l--1',
              styles.userMenu,
            )}
            fill='subtle'
            iconName='keyboard_arrow_down'
            iconPosition='right'
            onClick={() => setOpen(!open)}
            size='xsmall'
            aria-label='User Settings'
          >
            <GlobalNavAvatar />
          </Button>
        </Tooltip>
      }
      onClickOutside={() => setOpen(false)}
      data-testid='user-menu'
    >
      <ActionMenu.Group>
        <Link to='/profile'>
          <ActionMenu.Item onClick={handleNavigate}>
            {strings.menuItems.profile}
          </ActionMenu.Item>
        </Link>
        <ActionMenu.Item onClick={handleShowDiagnostics}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            {strings.menuItems.diagnostics}
            {unreadErrors.length > 0 && (
              <StatusLight className={styles.statusLight} color='critical' />
            )}
          </Stack>
        </ActionMenu.Item>
        {showNetworkTestMenuItem && (
          <ActionMenu.Item
            disabled={hasOngoingCall}
            id={mainLayoutLocators.userMenu.makeTestCallButton}
            onClick={handleMakeTestCall}
          >
            {strings.menuItems.makeTestCall}
          </ActionMenu.Item>
        )}
      </ActionMenu.Group>
      <ActionMenu.Group>
        {showRefreshUserStatusMenuItem && (
          <ActionMenu.Item
            id={mainLayoutLocators.userMenu.refreshStatusButton}
            onClick={handleRefreshUserStatus}
          >
            {strings.menuItems.refreshUserStatus}
          </ActionMenu.Item>
        )}
        <ActionMenu.Item data-testid='bug-report' onClick={handleBugReport}>
          {strings.menuItems.reportBug}
        </ActionMenu.Item>
      </ActionMenu.Group>
      <ActionMenu.Group>
        <ActionMenu.Item data-testid='sign-out' onClick={handleSignout}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <BodyText>{strings.menuItems.signOut}</BodyText>
            <BodyText
              className='c-neutral-100'
              id={mainLayoutLocators.userMenu.userNameLabel}
              italic
            >
              {user?.name}
            </BodyText>
          </Stack>
        </ActionMenu.Item>
      </ActionMenu.Group>
    </ActionMenu>
  );
}

export default UserMenu;
